import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputAdornment,
  OutlinedInput,
  Skeleton,
  Switch,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';

import styles from './styles';

const useStyles = makeStyles(styles);

export const DefaultsFormComponent = ({
  eventTemplate,
  statusEndId,
  handleChangeDueDateDefaultTime,
  handleChangeDueDateWorkingTime,
  handleChangeDateStatusChangeId,
  isLoading,
}) => {
  const classes = useStyles();

  const defaultDueDateDays = Math.floor(eventTemplate.dueDateDefaultTime / 8.64e7 || 0);
  const defaultDueDateHours = Math.floor((eventTemplate.dueDateDefaultTime % 8.64e7) / 3.6e6 || 0);
  const defaultDueDateMinutes = Math.floor(Math.round((eventTemplate.dueDateDefaultTime % 3.6e6) / 6e4) || 0);

  const handleDueDateChange = (days = 0, hours = 0, minutes = 0) => {
    const newValue = (86400000 * days) + (3600000 * hours) + (60000 * minutes);
    handleChangeDueDateDefaultTime(newValue < 0
      ? 0
      : newValue, false);
  };

  const handleDueDateWorkingTimeChange = (e) => {
    handleChangeDueDateWorkingTime({
      ...eventTemplate.dueDateWorkingTime,
      [e.target.name]: e.target.checked,
    });
  };

  if (isLoading) {
    return (
      <Card className={classes.card}>
        <CardHeader
          title="Task Completion Time"
          subheader="Configure time to complete started tasks."
        />
        <CardContent>
          <Skeleton height={40} sx={{ mb: 2 }} />
          <Skeleton height={40} sx={{ mb: 2 }} />
          <Skeleton height={40} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        title={(
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Track Task Completion Time
            <Switch
              checked={!!(eventTemplate.dueDateDefaultTime && eventTemplate.dueDateDefaultTime > 0)}
              onChange={(event) => {
                if (event.target.checked) {
                  handleDueDateChange(0, 2, 0);
                } else {
                  handleDueDateChange(0, 0, 0);
                }
              }}
              inputProps={{ 'aria-label': 'controlled' }}
              sx={{ ml: 1 }}
            />
          </Box>
        )}
        subheader="If turned on, Questri tracks when the task should be completed from the moment it starts."
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              md: (eventTemplate.dueDateDefaultTime && eventTemplate.dueDateDefaultTime > 0 && !eventTemplate.dueDateDefaultTimeEndOfDay)
                ? 6
                : 12,
            }}
          >
            <FormGroup>
              {(eventTemplate.dueDateDefaultTime && eventTemplate.dueDateDefaultTime > 0)
                ? (
                  <>
                    <FormControlLabel
                      sx={{ mb: 0 }}
                      control={<Switch />}
                      label="Complete task by end of day"
                      checked={!!eventTemplate.dueDateDefaultTimeEndOfDay}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleChangeDueDateDefaultTime(1, true);
                        } else {
                          handleDueDateChange(0, 12, 0);
                        }
                      }}
                    />
                    <FormHelperText>
                      Move task to Past Due if not completed by the end of the day it was created.
                    </FormHelperText>
                    <FormControlLabel
                      sx={{
                        mt: 2,
                        mb: 0,
                      }}
                      control={<Switch />}
                      label="Automatically complete when Past Due"
                      checked={!!eventTemplate.dueDateStatusChangeId}
                      onChange={(event) => {
                        if (event.target.checked) {
                          handleChangeDateStatusChangeId(statusEndId);
                        } else {
                          handleChangeDateStatusChangeId(0);
                        }
                      }}
                    />
                    <FormHelperText>
                      Automatically closes the task if the completion window is exceeded.
                    </FormHelperText>
                  </>
                )
                : (
                  <Typography variant="caption" color="text.secondary">
                    Enable tracking to configure due time targets for this task type.
                  </Typography>
                )}
            </FormGroup>
          </Grid>
          {(eventTemplate.dueDateDefaultTime && eventTemplate.dueDateDefaultTime > 0 && !eventTemplate.dueDateDefaultTimeEndOfDay)
            ? (
              <Grid
                container
                spacing={2}
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <Grid size={{ xs: 4 }}>
                  <FormControl variant="outlined" className={classes.defaultDueDateTimeInput}>
                    <OutlinedInput
                      label=" "
                      id="default-due-date-days"
                      type="number"
                      margin="dense"
                      value={defaultDueDateDays}
                      onChange={(e) => { handleDueDateChange(parseInt(e.target.value || 0, 10), defaultDueDateHours, defaultDueDateMinutes); }}
                      endAdornment={<InputAdornment position="end">days</InputAdornment>}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <FormControl variant="outlined" className={classes.defaultDueDateTimeInput}>
                    <OutlinedInput
                      label=" "
                      id="default-due-date-hours"
                      type="number"
                      margin="dense"
                      value={defaultDueDateHours}
                      onChange={(e) => { handleDueDateChange(defaultDueDateDays, parseInt(e.target.value || 0, 10), defaultDueDateMinutes); }}
                      endAdornment={<InputAdornment position="end">hours</InputAdornment>}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <FormControl variant="outlined" className={classes.defaultDueDateTimeInput}>
                    <OutlinedInput
                      label=" "
                      id="default-due-date-minutes"
                      type="number"
                      margin="dense"
                      value={defaultDueDateMinutes}
                      onChange={(e) => { handleDueDateChange(defaultDueDateDays, defaultDueDateHours, parseInt(e.target.value || 0, 10)); }}
                      endAdornment={<InputAdornment position="end">minutes</InputAdornment>}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth component="fieldset" className={classes.dueDateWorkingTimeFormControl}>
                    <FormLabel>Include days in completion window</FormLabel>
                    <FormGroup row>
                      {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map((day) => (
                        <FormControlLabel
                          key={day}
                          control={(
                            <Checkbox
                              checked={!eventTemplate.dueDateWorkingTime || eventTemplate.dueDateWorkingTime[day] !== false}
                              onChange={handleDueDateWorkingTimeChange}
                              name={day}
                            />
                          )}
                          label={day.slice(0, 3)}
                        />
                      ))}
                    </FormGroup>
                    <FormHelperText>Select or deselect days that should count toward the completion time.</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            )
            : <Box />}
        </Grid>
      </CardContent>
    </Card>
  );
};

DefaultsFormComponent.propTypes = {
  eventTemplate: PropTypes.object.isRequired,
  statusEndId: PropTypes.number.isRequired,
  handleChangeDueDateDefaultTime: PropTypes.func,
  handleChangeDueDateWorkingTime: PropTypes.func,
  handleChangeDateStatusChangeId: PropTypes.func,
  isLoading: PropTypes.bool,
};

DefaultsFormComponent.defaultProps = {
  handleChangeDueDateDefaultTime: () => { },
  handleChangeDueDateWorkingTime: () => { },
  handleChangeDateStatusChangeId: () => { },
  isLoading: false,
};

export default DefaultsFormComponent;
